/**
 * Base interface for all components
 * Components are pure data containers
 */
export interface Component {
  readonly type: string;
}

/**
 * Component constructor type for type-safe queries
 */
export interface ComponentClass<T extends Component = Component> {
  new (...args: unknown[]): T;
  readonly type: string;
}

/**
 * Create a component class with a type identifier
 */
export function defineComponent<T extends object>(
  type: string
): ComponentClass<T & Component> {
  return class implements Component {
    static readonly type = type;
    readonly type = type;

    constructor(data: T) {
      Object.assign(this, data);
    }
  } as ComponentClass<T & Component>;
}

// Example components for the game

export interface PositionData {
  q: number;
  r: number;
}

export const Position = defineComponent<PositionData>('position');
export type Position = InstanceType<typeof Position>;

export interface HealthData {
  current: number;
  max: number;
}

export const Health = defineComponent<HealthData>('health');
export type Health = InstanceType<typeof Health>;

export interface AttackData {
  damage: number;
  range: number;
  cooldown: number;
  currentCooldown: number;
}

export const Attack = defineComponent<AttackData>('attack');
export type Attack = InstanceType<typeof Attack>;

export interface RenderData {
  meshId: string;
  color: number;
}

export const Render = defineComponent<RenderData>('render');
export type Render = InstanceType<typeof Render>;

export interface TeamData {
  playerId: number;
}

export const Team = defineComponent<TeamData>('team');
export type Team = InstanceType<typeof Team>;
